import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Login from "./pages/Login";
import MetricDetail from "./pages/MetricDetail";
import RecoveryPage from "./pages/RecoveryPage";
import WipPage from "./pages/WipPage";
import WipDrillThroughTablesPage from "./pages/WipDrillThroughTablesPage";
import LockupPage from "./pages/LockupPage";
import LockupDrillThroughTablesPage from "./pages/LockupDrillThroughTablesPage";
import DebtorsPage from "./pages/DebtorsPage";
import DebtorsDrillThroughTablesPage from "./pages/DebtorsDrillThroughTablesPage";
import UtilisationTablesPage from "./pages/UtilisationTablesPage";
import RecoveryTablesPage from "./pages/RecoveryTablesPage";
import FAQ from "./pages/FAQ";
import AdminPage from "./pages/AdminPage";
import DatacoreLayout from "./pages/DatacoreLayout";
import DataUploadsPage from "./pages/DataUploadsPage";
import XeroIntegrationSection from "./pages/XeroIntegrationSection";
import XeroOrganisationsLogPage from "./pages/XeroOrganisationsLogPage";
import XeroMappingsLogPage from "./pages/XeroMappingsLogPage";
import CchIntegrationSection from "./pages/CchIntegrationSection";
import Profile from "./pages/Profile";
import SetupPassword from "./pages/SetupPassword";
import SSOCallback from "./pages/SSOCallback";
import XeroCallback from "./pages/XeroCallback";
import NotFound from "./pages/NotFound";
import { RequireAdmin } from "./components/RequireAdmin";
import { RequirePasswordChange } from "./components/RequirePasswordChange";
import { ServerStatusGate } from "./components/ServerStatusGate";

const queryClient = new QueryClient();

function AuthLogoutHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => navigate("/", { replace: true });
    window.addEventListener("auth:logout", handler);
    return () => window.removeEventListener("auth:logout", handler);
  }, [navigate]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem storageKey="prism-theme">
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthLogoutHandler />
        <ServerStatusGate>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/dashboard" element={<Navigate to="/metrics/utilisation-rate" replace />} />
          <Route path="/setup-password" element={<SetupPassword />} />
          <Route path="/auth/callback" element={<SSOCallback />} />

          <Route
            path="/metrics/recovery-rate"
            element={
              <RequirePasswordChange>
                <RecoveryPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/wip-aged"
            element={
              <RequirePasswordChange>
                <WipPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/wip-aged/drill-through"
            element={
              <RequirePasswordChange>
                <WipDrillThroughTablesPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/debt-aged"
            element={
              <RequirePasswordChange>
                <DebtorsPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/lockup"
            element={
              <RequirePasswordChange>
                <LockupPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/lockup/drill-through"
            element={
              <RequirePasswordChange>
                <LockupDrillThroughTablesPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/recovery-rate/drill-through"
            element={
              <RequirePasswordChange>
                <RecoveryTablesPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/debtors-current/drill-through"
            element={
              <RequirePasswordChange>
                <DebtorsDrillThroughTablesPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/utilisation-rate/drill-through"
            element={
              <RequirePasswordChange>
                <UtilisationTablesPage />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/metrics/:metricSlug"
            element={
              <RequirePasswordChange>
                <MetricDetail />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/faq"
            element={
              <RequirePasswordChange>
                <FAQ />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/profile"
            element={
              <RequirePasswordChange>
                <Profile />
              </RequirePasswordChange>
            }
          />
          <Route
            path="/admin"
            element={
              <RequirePasswordChange>
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              </RequirePasswordChange>
            }
          />
          
          <Route
            path="/datacore"
            element={
              <RequirePasswordChange>
                <RequireAdmin>
                  <DatacoreLayout />
                </RequireAdmin>
              </RequirePasswordChange>
            }
          >
            <Route index element={<Navigate to="data-uploads" replace />} />
            <Route path="data-uploads" element={<DataUploadsPage />} />
            <Route path="xero" element={<XeroIntegrationSection />} />
        
            <Route path="xero/organisations/logs" element={<XeroOrganisationsLogPage />} />
            <Route path="xero/mappings/logs" element={<XeroMappingsLogPage />} />
            <Route path="cch" element={<CchIntegrationSection />} />
          </Route>
        
          <Route
            path="/data-uploads"
            element={<Navigate to="/datacore/data-uploads" replace />}
          />

          <Route
            path="/admin/integrations/xero"
            element={
              <RequirePasswordChange>
                <RequireAdmin>
                  <XeroCallback />
                </RequireAdmin>
              </RequirePasswordChange>
            }
          />
        
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ServerStatusGate>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
